/**
 * Card-attachments API client (0162 / ADR-0048 — the work-item drawer's
 * Attachments section; design of record:
 * build-specs/card-attachments-design.md §6.3).
 *
 * A standalone client in the src/lib/documents.ts idiom, deliberately
 * NOT a method group on workItemsApi: the upload path needs the
 * direct-to-Blob fetch plumbing that lives outside api.ts's wrapper.
 * Native fetch, ApiError normalization, redirect:'manual' on writes for
 * the X.5.4 auth-expiry signal.
 *
 *   POST   /api/t/{slug}/v1/work-items/{id}/attachments          handshake step 1
 *   (PUT the bytes to the returned single-use URL)               handshake step 2
 *   POST   /api/t/{slug}/v1/attachments/{attachmentId}/complete  handshake step 3
 *   GET    /api/t/{slug}/v1/work-items/{id}/attachments          the drawer list (active only)
 *   POST   /api/t/{slug}/v1/attachments/{attachmentId}/download  the short-lived read grant
 *   DELETE /api/t/{slug}/v1/attachments/{attachmentId}           hard delete (row + stored file)
 *
 * THE ALLOWLIST IS NOT THE CLIENT-DOCUMENTS ONE. Card attachments take
 * exactly FOUR types (pdf, docx, png, jpeg); `text/plain` is accepted on
 * the clinical Documents tab and is deliberately EXCLUDED here (founder
 * ruling 2, 2026-07-25 — design §5.1). ATTACHMENT_ACCEPT is therefore
 * its own constant, never a re-export of DOCUMENT_ACCEPT, which would
 * silently re-admit .txt. A unit test pins that the two sets differ.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';
import { putFileBytes, sha256HexOf } from './blobUpload';

// ---------------------------------------------------------------------
// Wire shapes (mirror api/_shared/repos/workItemAttachments.ts)
// ---------------------------------------------------------------------

export interface WorkItemAttachmentWire {
  id: string;
  work_item_id: string;
  display_name: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  /** Resolved server-side — a raw uuid never reaches the screen. */
  uploaded_by_display_name: string | null;
  /** Deep links only; never rendered. */
  uploaded_by_user_id: string;
}

export interface AttachmentsListResponse {
  rows: WorkItemAttachmentWire[];
}

export interface CreateAttachmentResponse {
  attachment_id: string;
  upload_url: string;
  upload_expires_at: string;
}

export interface AttachmentDownloadGrantResponse {
  attachment_id: string;
  work_item_id: string;
  download_url: string;
  download_expires_at: string;
  display_name: string;
  content_type: string;
  /** The EFFECTIVE (server-coerced) disposition — the client never decides. */
  disposition: AttachmentDisposition;
}

export interface DeleteAttachmentResponse {
  attachment_id: string;
  deleted: true;
}

// ---------------------------------------------------------------------
// Upload-side policy (mirrors api/_shared/validators/workItemAttachments.ts)
// ---------------------------------------------------------------------

export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * The founder-gated closed set: exactly four types. NARROWER than
 * src/lib/documents.ts ACCEPTED_TYPES on purpose — text/plain is out.
 */
export const ATTACHMENT_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
]);

/**
 * The picker's accept attribute (extensions + mimes). Its OWN constant:
 * re-exporting DOCUMENT_ACCEPT would re-admit .txt behind the design.
 */
export const ATTACHMENT_ACCEPT =
  '.pdf,.docx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg';

/**
 * The PREVIEWABLE subset — the three types a browser renders natively
 * and safely in a tab (mirror of ATTACHMENT_PREVIEWABLE_CONTENT_TYPES).
 * docx is deliberately absent: there is no safe inline path for it, so
 * the server coerces an inline request for one back to a download.
 */
export const ATTACHMENT_PREVIEWABLE_CONTENT_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
]);

/** Whether the row's lead affordance is Open (else Download). */
export function isAttachmentPreviewable(contentType: string): boolean {
  return ATTACHMENT_PREVIEWABLE_CONTENT_TYPES.has(contentType);
}

/** How the grant presents the bytes (mirror of the api-side type). */
export type AttachmentDisposition = 'attachment' | 'inline';

const EXTENSION_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

/**
 * The file's effective content type: the browser-reported type when it
 * is accepted, else an extension fallback (docx in particular reads as
 * empty or octet-stream on some systems). null = not an accepted type,
 * which the section surfaces BEFORE any upload starts.
 */
export function resolveAttachmentContentType(file: { name: string; type: string }): string | null {
  if (ATTACHMENT_CONTENT_TYPES.has(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return EXTENSION_TYPES[ext] ?? null;
}

// ---------------------------------------------------------------------
// Fetch plumbing (documents.ts / notes.ts semantics)
// ---------------------------------------------------------------------

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text().catch(() => null);
  }
}

async function get<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `GET ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function post<T>(
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
    signal,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `POST ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function del<T>(slug: string, path: string): Promise<T> {
  const res = await writeFetch(tenantUrl(slug, path), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
  });
  if (!res.ok) {
    throw new ApiError(res.status, await parseErrorBody(res), `DELETE ${path} reached HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------
// The API surface
// ---------------------------------------------------------------------

export const workItemAttachmentsApi = {
  list(slug: string, workItemId: string, signal?: AbortSignal): Promise<AttachmentsListResponse> {
    return get(slug, `/work-items/${encodeURIComponent(workItemId)}/attachments`, signal);
  },

  /**
   * The whole three-step handshake behind ONE mutation: declare (pending
   * row + single-blob write URL) then PUT the bytes then complete
   * (verify + flip active). Throws ApiError at whichever step failed; a
   * failed step leaves at most a harmless pending row server-side, so
   * retry means running it again.
   */
  async uploadAttachment(
    slug: string,
    workItemId: string,
    file: File,
  ): Promise<{ attachment_id: string }> {
    const contentType = resolveAttachmentContentType(file);
    if (contentType === null) {
      throw new ApiError(
        400,
        { error: 'invalid_body', field: 'content_type' },
        'file type not accepted for a card attachment',
      );
    }
    if (file.size <= 0 || file.size > ATTACHMENT_MAX_BYTES) {
      throw new ApiError(400, { error: 'invalid_body', field: 'size_bytes' }, 'file too large');
    }

    const declared = await post<CreateAttachmentResponse>(
      slug,
      `/work-items/${encodeURIComponent(workItemId)}/attachments`,
      { display_name: file.name, content_type: contentType, size_bytes: file.size },
    );

    await putFileBytes(declared.upload_url, file, contentType);

    const sha256_hex = await sha256HexOf(file);
    await post(slug, `/attachments/${encodeURIComponent(declared.attachment_id)}/complete`, {
      sha256_hex,
    });

    return { attachment_id: declared.attachment_id };
  },

  /**
   * The short-lived single-file read grant. 'inline' means open in a
   * tab, and is honored only for previewable types — the server coerces
   * anything else (docx) back to a plain download against the STORED
   * content type, so this argument is a UX nicety, never the control.
   */
  downloadGrant(
    slug: string,
    attachmentId: string,
    disposition: AttachmentDisposition = 'attachment',
  ): Promise<AttachmentDownloadGrantResponse> {
    return post(slug, `/attachments/${encodeURIComponent(attachmentId)}/download`, {
      disposition,
    });
  },

  /** Hard delete: the row, then the stored file. Permanent (JC-4). */
  deleteAttachment(slug: string, attachmentId: string): Promise<DeleteAttachmentResponse> {
    return del(slug, `/attachments/${encodeURIComponent(attachmentId)}`);
  },
};
